// index.js (Workflow Component)
import React, { useState, useContext, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { Icon as SemanticIcon } from "semantic-ui-react";
import Home from "./Home";
import RiskHome from "./Workflows/RiskHome";
import ROI from "./Workflows/ROIHome";
import Thematic from "./Workflows/Thematic";
import Change from "./Workflows/Change";
import { CopilotContext, CopilotProvider } from "./CopilotContext";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import DeleteModal from "new_components/organisms/DeleteModal";
import colors from "styles/colors";
import ImageButton from "new_components/molecules/ImageButton";
import Tooltip from "new_components/molecules/Tooltip";
import SkeletonLoader from "new_components/organisms/SkeletonLoader";

function HistorySidebar({
  history,
  onSelectSession,
  onGoHome,
  isOpen,
  onDeletePending,
  isLoading,
}) {
  const getTitle = (item) => {
    if (item.type == "risk" && item?.selectedFactors?.type == "Culture") {
      return `${item.selectedFactors.type} - ${item.type}`;
    }
    if (item.selectedFactors?.type == "Overview") {
      return "Overview";
    } else if (item.selectedFactors?.title) {
      return `${item.selectedFactors?.title} - ${item.type}`;
    } else if (item.type == "change") {
      return `Change Analysis`;
    } else {
      return `${item.selectedFactors?.name} - ${item.type}`;
    }
  };

  return (
    <SidebarContainer isOpen={isOpen}>
      <SidebarHeader>
        <SidebarTitle>Workflow History</SidebarTitle>
        <IconGroup>
          <Tooltip tipPosition="bottom" tooltipText="Start new workflow">
            <ActionIcon
              name="plus"
              onClick={onGoHome}
              title="Start new workflow"
            />
          </Tooltip>
        </IconGroup>
      </SidebarHeader>
      <HistoryList>
        {!isLoading && history.length === 0 ? (
          <EmptyState>No previous sessions found</EmptyState>
        ) : (
          <SkeletonLoader isLoading={isLoading}>
            {history.map((item) => (
              <SessionItem onClick={() => onSelectSession(item)}>
                <InfoWrapper>
                  <SessionTitle>{getTitle(item)}</SessionTitle>

                  {/* Date & filters in one row to reduce vertical space */}
                  <MetaRow>
                    <SessionDate>
                      {new Date(item?.created).toLocaleDateString()}
                    </SessionDate>
                    {item?.filters?.length > 0 && (
                      <Filters>
                        {item.filters.map((filter, idx) => (
                          <Filter key={idx}>{filter.name}</Filter>
                        ))}
                      </Filters>
                    )}
                  </MetaRow>
                </InfoWrapper>

                <DeleteWrapper
                  onClick={(event) => {
                    event.stopPropagation(); // prevent item click
                    onDeletePending(item.itemId);
                  }}
                >
                  <Tooltip tooltipText="Delete workflow">
                    <Icon name={ICON_SET.delete} />
                  </Tooltip>
                </DeleteWrapper>
              </SessionItem>
            ))}
          </SkeletonLoader>
        )}
      </HistoryList>
    </SidebarContainer>
  );
}

const Workflow = () => {
  const [workflow, setWorkflow] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {
    addChatWorkflowHistory,
    workflowData,
    deleteChatWorkflowHistory,
    setActiveWorkflow,
  } = useContext(CopilotContext);

  // For showing/hiding the Scroll-to-bottom button
  const scrollRef = useRef(null);
  const endOfMessagesRef = useRef(null);

  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);

  const handleSaveSession = (sessionData) => {
    const newSession = { ...sessionData, created: Date.now() };
    setHistory((prev) => [newSession, ...prev]);
    // { name: '', content: [], params: {} }
    const payload = {
      name: newSession.type,
      content: [newSession],
      params: {},
    };
    addChatWorkflowHistory(payload);
  };

  const handleSelectSession = (session) => {
    // If session is an array, get the first index.
    if (Array.isArray(session)) {
      session = session[0];
    }

    setCurrentSession(session);
    setActiveWorkflow(session);

    setWorkflow(session.type);
    setIsSidebarOpen(false);
  };

  const handleGoHome = () => {
    setWorkflow(null);
    setCurrentSession(null);
    setIsSidebarOpen(true);
    setActiveWorkflow(null);
  };

  /**
   * 4) (REMOVED auto-scroll)
   *    Instead, we attach a scroll listener to show/hide the button.
   */
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;

      const threshold = 100;
      if (scrollHeight - scrollTop - clientHeight < threshold) {
        setShowScrollButton(false);
      } else {
        setShowScrollButton(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Manual scroll to bottom
  const scrollToBottom = () => {
    // Use window and scroll to bottom.
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (workflowData && workflowData?.results?.length > 0) {
      const history = workflowData?.results
        ?.filter((item) => item?.content?.length > 0)
        ?.flatMap((item) => {
          return { ...item.content[0], itemId: item.id, id: item.id };
        });

      // Order by created_at desc
      history.sort((a, b) => b.created - a.created);

      setHistory(history);
    }
  }, [workflowData]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  let mainArea;
  if (!workflow) {
    mainArea = (
      <Home
        setWorkflow={(type) => {
          setWorkflow(type);
          setIsSidebarOpen(false);
        }}
      />
    );
  } else if (workflow === "risk") {
    mainArea = (
      <RiskHome
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
      />
    );
  } else if (workflow === "roi") {
    mainArea = (
      <ROI
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
      />
    );
  } else if (workflow === "thematic") {
    mainArea = (
      <Thematic
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
      />
    );
  } else if (workflow === "change") {
    mainArea = (
      <Change
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
      />
    );
  }

  const handleBack = () => {
    setWorkflow(null);
    setCurrentSession(null);
  };

  return (
    <AppContainer ref={scrollRef}>
      <HistorySidebar
        history={history}
        onSelectSession={handleSelectSession}
        onGoHome={handleGoHome}
        isOpen={isSidebarOpen}
        isLoading={isLoading}
        onDeletePending={setIsDeletePending}
      />

      <DeleteModal
        isOpen={isDeletePending}
        onClose={() => setIsDeletePending(false)}
        onConfirm={() => {
          setHistory(history.filter((item) => item.itemId !== isDeletePending));
          deleteChatWorkflowHistory(isDeletePending);
          setIsDeletePending(false);
          setCurrentSession(null);
          setWorkflow(null);
        }}
      />
      <SidebarToggleButton
        onClick={toggleSidebar}
        isOpen={isSidebarOpen}
        aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
      >
        <ToggleIcon name={isSidebarOpen ? "chevron left" : "chevron right"} />
      </SidebarToggleButton>

      <MainWorkspace isOpen={isSidebarOpen}>
        {workflow && (
          <ActionRow show={true}>
            <ImageButton
              text={"Back"}
              textStyle={true}
              icon={ICON_SET.chevronLeft}
              onClick={handleBack}
              color={colors.text.gray}
            />
          </ActionRow>
        )}

        {mainArea}
        <div ref={endOfMessagesRef} />
      </MainWorkspace>
      {/* Show the scroll-to-bottom button only when needed */}
      {showScrollButton && (
        <ScrollToBottomButton onClick={scrollToBottom}>
          <SemanticIcon style={{ paddingLeft: 3 }} name="arrow down" />
        </ScrollToBottomButton>
      )}
    </AppContainer>
  );
};

export const WorkflowProvider = ({ children }) => {
  return (
    <CopilotProvider>
      <Workflow />
    </CopilotProvider>
  );
};

export default WorkflowProvider;

// Styled Components
const AppContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f7f9;
  position: relative;
`;


const SidebarContainer = styled.div`
  width: 280px;
  height: 100vh;
  background: #ffffff;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 200px;
  transform: translateX(${(props) => (props.isOpen ? "0" : "-100%")});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
  display: flex;
  flex-direction: column;
  font-family: "Raleway", sans-serif;
`;

// Wrapper for the action row
const ActionRow = styled.div`
  display: ${({ show }) => (show ? "flex" : "none")};
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  margin-bottom: 16px;
`;

const SidebarHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e8ebee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #2c3e50;
  font-weight: 600;
`;

const IconGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionIcon = styled(SemanticIcon)`
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s ease;

  &:hover {
    color: #2c3e50;
  }
`;

const HistoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;
const SessionItem = styled.div`
  /* Use a tighter spacing approach */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  background: ${(props) => (props.isActive ? "#f8f9fa" : "#ffffff")};
  cursor: pointer;
  transition: background 0.2s ease;

  /* Subtle hover effect */
  &:hover {
    background: #f2f4f5;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SessionTitle = styled.div`
  font-size: 0.875rem; /* 14px equivalent if base is 16px */
  font-weight: 500;
  color: #2a3039;
  margin-bottom: 4px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; /* space between date and filters */
`;

const SessionDate = styled.div`
  font-size: 0.75rem; /* 12px */
  color: #95a5a6;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Filter = styled.span`
  background-color: #ecf0f1;
  color: #2c3e50;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
`;

const DeleteWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #bdc3c7;

  &:hover {
    color: #c0392b;
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #95a5a6;
  font-size: 0.9rem;
`;

const SidebarToggleButton = styled.button`
  position: fixed;
  left: ${(props) => (props.isOpen ? "480px" : "200px")};
  top: 24px;
  width: 32px;
  height: 32px;
  background: #ffffff;
  border: none;
  border-radius: 0 6px 6px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${(props) => (props.isOpen ? "0" : "0")});

  &:hover {
    background: #f8f9fa;
    transform: translateX(${(props) => (props.isOpen ? "4px" : "4px")});
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(44, 62, 80, 0.1);
  }
`;

const ToggleIcon = styled(SemanticIcon)`
  color: #2c3e50;
  margin: 0 !important;
`;

const MainWorkspace = styled.main`
  flex: 1;
  margin-left: ${(props) => (props.isOpen ? "240px" : "0")};
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 100vh;
  padding: 32px;
  background-color: #ffffff;
`;


const ScrollToBottomButton = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #1678c2; /* or any color you prefer */
  color: #fff;
  padding: 10px 16px;
  border-radius: 24px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;